import { siteApiSlice } from "../../app/siteApiSlice";
import NamedEntity from "../../interfaces/NamedEntity";

export interface GetSchoolsRequestModel {
	districtCode: string;
}

export interface SelectDistrictSpaViewModel {
	id: number;
	name: string;
	schoolCount: number;
	code: string;
	cleverId: string;
	classLinkId: string;
}

export interface DistrictRowSpaViewModel {
	districtID: number;
	districtCode: string;
	districtName: string;
	city: string;
	state: string;
	emailAddress: string;
	phone: string;
	externalID: string;
	schoolCount: number;
	cleverID: string;
	classLinkID: string;
}

export interface DistrictCreateEditModel {
	id: number;
	name: string;
	state: string;
	address: string;
	city: string;
	zip: string;
	phone: string;
	code: string;
	emailAddress: string;
	externalID: string;
	cleverID: string;
	showCleverId: boolean;
	classLinkID: string;
	showClassLinkId: boolean;
	disableUserImages: boolean;
	isIncludedInReports: boolean;
	takenSeats: number;
	totalSeats: number;
}

export interface DistrictSchoolListQueryModel {
	districtId: number;
	active: string;
}

export interface DistrictSchoolsResult {
	totalSeatsInUse: number;
	totalSeatsAllocated: number;
	schools: DistrictSchoolListViewModel[];
}

export interface DistrictSchoolListViewModel {
	id: number;
	externalID: string;
	name: string;
	active: boolean;
	expirationDate: Date;
	hasExpired: boolean;
	contact: string;
	phone: string;
	numCourses: number;
	seatsInUse: number;
	seatsAllocated: number;
	isCleverSchool: boolean;
	isClassLinkSchool: boolean;
}

export interface DistrictAdminViewModel {
	id: number;
	userName: string;
	formalFullName: string;
	phoneNumber: string;
	email: string;
	initials: string;
	hasImage: boolean;
	lastLoginDate: Date;
	disabled: boolean;
}

export interface DistrictCleverInfoResult {
	cleverId: string;
	lastSyncAt: Date;
}

export interface UpdateDistrictCleverSettingsViewModel {
	districtId: number;
	cleverId: string;
}

export interface DistrictClassLinkInfoResult {
	tenantId: string;
	classLinkId: string;
	clientId: string;
	clientSecret: string;
	endpointUrl: string;
	syncStatus: string;
	syncStartTime: Date;
	syncFinishTime: Date;
}

export interface UpdateDistrictClassLinkSettingsViewModel {
	districtId: number;
	tenantId: string;
	classLinkID: string;
	clientId: string;
	clientSecret: string;
	endpointUrl: string;
	syncStatus: string;
}

export const districtsApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["Districts", "District", "CleverSettings", "ClassLinkSettings"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getDistricts: build.query<NamedEntity[], void>({
				query: () => ({
					url: `/District/GetDistricts`,
				}),
				providesTags: ["Districts"],
			}),
			getSchools: build.mutation<NamedEntity[], GetSchoolsRequestModel>({
				query: (payload) => ({
					url: `/District/GetSchools`,
					method: "POST",
					body: payload,
				}),
			}),
			getSchoolsByDistrictId: build.mutation<
				NamedEntity[],
				{ districtId?: number; filterInactive: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/GetSchoolsByDistrictId`,
						method: "POST",
						body: payload,
					};
				},
			}),
			getDistrictPickerOptions: build.query<NamedEntity[], void>({
				query: () => ({
					url: `/District/DistrictPickerOptions`,
				}),
			}),
			createDistrict: build.mutation<void, DistrictCreateEditModel>({
				query: (data) => {
					return {
						url: `/District/Create`,
						method: "POST",
						body: data,
					};
				},
				invalidatesTags: ["Districts"],
			}),
			getDistrictInfo: build.query<DistrictCreateEditModel, number>({
				query: (id) => ({
					url: `/District/Edit`,
					params: { id },
				}),
				providesTags: ["District"],
			}),
			editDistrict: build.mutation<void, DistrictCreateEditModel>({
				query: (data) => {
					return {
						url: `/District/Edit`,
						method: "POST",
						body: data,
					};
				},
				invalidatesTags: ["Districts", "District"],
			}),
			getSelectDistrictOptions: build.query<SelectDistrictSpaViewModel[], void>({
				query: () => ({
					url: `/SignIn/SelectDistrict`,
				}),
			}),
			makeDistrictSelection: build.mutation<void, number>({
				query: (districtId) => ({
					url: `/SignIn/MakeDistrictSelection/${districtId}`,
					method: "GET",
				}),
				invalidatesTags: ["CurrentDistrict"],
			}),
			getDistrictList: build.query<DistrictRowSpaViewModel[], void>({
				query: () => ({
					url: `/District/List`,
					method: "GET",
				}),
				providesTags: ["Districts"],
			}),
			updateUserAvatarSettingForDistrict: build.mutation<
				void,
				{ districtId: number; disabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/UpdateAvatarSettingForDistrict`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["District"],
			}),
			updateReportSettingForDistrict: build.mutation<
				void,
				{ districtId: number; enabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/District/UpdateReportSettingForDistrict`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["District"],
			}),
			schoolListByDistrict: build.query<DistrictSchoolsResult, DistrictSchoolListQueryModel>({
				query: (q) => {
					const { districtId, active: active } = q;
					return {
						url: `/District/SchoolListByDistrict`,
						params: {
							districtId,
							active,
						},
					};
				},
				providesTags: ["Schools"],
			}),
			adminListByDistrict: build.query<DistrictAdminViewModel[], number>({
				query: (districtId) => {
					return {
						url: `/District/DistrictAdminListByDistrict`,
						params: {
							districtId,
						},
					};
				},
			}),
			getCleverSettingsInfo: build.query<DistrictCleverInfoResult, number>({
				query: (districtId) => ({
					url: `/District/EditCleverSettings`,
					method: "GET",
					params: { districtId },
				}),
				providesTags: ["CleverSettings"],
			}),
			updateCleverSettingsInfo: build.mutation<void, UpdateDistrictCleverSettingsViewModel>({
				query: (payload) => ({
					url: `/District/EditCleverSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Districts", "District", "CleverSettings"],
			}),
			getClassLinkSettingsInfo: build.query<DistrictClassLinkInfoResult, number>({
				query: (districtId) => ({
					url: `/District/EditClassLinkSettings`,
					method: "GET",
					params: { districtId },
				}),
				providesTags: ["ClassLinkSettings"],
			}),
			updateClassLinkSettingsInfo: build.mutation<
				void,
				UpdateDistrictClassLinkSettingsViewModel
			>({
				query: (payload) => ({
					url: `/District/EditClassLinkSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Districts", "District", "ClassLinkSettings"],
			}),
		}),
	});

export const {
	useGetDistrictsQuery,
	useGetSchoolsMutation,
	useGetSchoolsByDistrictIdMutation,
	useGetDistrictPickerOptionsQuery,
	useGetSelectDistrictOptionsQuery,
	useMakeDistrictSelectionMutation,
	useGetDistrictListQuery,
	useCreateDistrictMutation,
	useGetDistrictInfoQuery,
	useEditDistrictMutation,
	useUpdateUserAvatarSettingForDistrictMutation,
	useUpdateReportSettingForDistrictMutation,
	useSchoolListByDistrictQuery,
	useAdminListByDistrictQuery,
	useGetCleverSettingsInfoQuery,
	useUpdateCleverSettingsInfoMutation,
	useGetClassLinkSettingsInfoQuery,
	useUpdateClassLinkSettingsInfoMutation,
} = districtsApiSlice;
